import React from 'react';
import { cn } from '@/lib/utils';
import { Label, NumberInput, Switch } from '@/components/ui';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { AnimatedFormError } from '@/components/form/AnimatedFormError';
import type { MealBreakConfig, Role } from '@/types';
import type { ExtendedTier } from '@/types/ExtendedTier';
import { CustomizedFieldIndicator } from '../../../form/CustomizedFieldIndicator';
import { ExtendedOvertimeSection } from '../../shared/ExtendedOvertimeSection';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';

interface Props {
  safeConfig: MealBreakConfig;
  onMealConfigChange: (updates: Partial<MealBreakConfig>) => void;
  stepUpAfterMinutesProps?: Record<string, unknown>;
  stepUpAfterMinutesError?: React.ReactNode;
  mealConfigCustomized: Partial<Record<keyof MealBreakConfig, boolean>>;
  roles?: Role[];
  minimumBillableHours?: number | null;
  context: 'user' | 'client';
  extendedOvertimeEnabled: boolean;
  onExtendedOvertimeEnabledChange: (enabled: boolean) => void;
  extendedOvertimeTiers: ExtendedTier[];
  onExtendedOvertimeTiersChange: (tiers: ExtendedTier[]) => void;
  highestOvertimeRate: number;
  highestOvertimeTierName?: string;
  defaultTiers?: ExtendedTier[];
  maxTiers?: number;
}

export const MultiplierOffsetSection: React.FC<Props> = ({
  safeConfig,
  onMealConfigChange,
  stepUpAfterMinutesProps,
  stepUpAfterMinutesError,
  mealConfigCustomized,
  context,
  extendedOvertimeEnabled,
  onExtendedOvertimeEnabledChange,
  extendedOvertimeTiers,
  onExtendedOvertimeTiersChange,
  highestOvertimeRate,
  highestOvertimeTierName,
  defaultTiers,
  maxTiers,
}) => {
  return (
    <div className="space-y-4">

      <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="use-escalation" className={fieldLabelClass}>Additional Offset</Label>
              <CustomizedFieldIndicator isCustomized={mealConfigCustomized.useAdditionalOffset} context={context} />
            </div>
          </div>
          <p className={cn(fieldDescriptionClass, "mt-1 hidden md:block")}>After a set time, apply an additional offset up the rate ladder for a stronger surcharge.</p>
        </div>
        <div className="flex justify-end md:justify-end md:w-full">
          <Switch
            id="use-escalation"
            checked={!!safeConfig.useAdditionalOffset}
            onCheckedChange={(checked) => onMealConfigChange({ useAdditionalOffset: checked })}
          />
        </div>
        <p className={cn(fieldDescriptionClass, "col-span-2 md:hidden")}>After a set time, apply an additional offset up the rate ladder for a stronger surcharge.</p>
      </div>

      <AnimatedCollapse open={!!safeConfig.useAdditionalOffset}>
        <div className="pl-4 border-l-2 border-muted">
          <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="escalation-threshold-minutes" className={fieldLabelClass}>Step-Up Deadline</Label>
                <CustomizedFieldIndicator isCustomized={mealConfigCustomized.stepUpAfterMinutes} context={context} />
              </div>
              <p className={cn(fieldDescriptionClass, "mt-1 hidden md:block")}>
                How long a missed break surcharge runs at the initial rate before stepping up to the next level.
              </p>
            </div>
            <div className="flex flex-col items-end md:items-end md:w-full">
              <NumberInput
                id="escalation-threshold-minutes"
                unit="min"
                size="xs"
                {...(stepUpAfterMinutesProps || {})}
              />
              <div className="w-full text-left md:text-right mt-1">
                <AnimatedFormError className="w-full text-left md:text-right mt-1">
                  {stepUpAfterMinutesError}
                </AnimatedFormError>
              </div>
            </div>
            <p className={cn(fieldDescriptionClass, "col-span-2 md:hidden")}>
              How long a missed break surcharge runs at the initial rate before stepping up to the next level.
            </p>
          </div>
        </div>
      </AnimatedCollapse>

      <ExtendedOvertimeSection
        enabled={extendedOvertimeEnabled}
        onEnabledChange={onExtendedOvertimeEnabledChange}
        tiers={extendedOvertimeTiers}
        onTiersChange={onExtendedOvertimeTiersChange}
        highestOvertimeRate={highestOvertimeRate}
        highestOvertimeTierName={highestOvertimeTierName}
        isCustomized={mealConfigCustomized.extendedOvertimeEnabled}
        context={context}
        defaultTiers={defaultTiers}
        maxTiers={maxTiers}
      />
    </div>
  );
};
